import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useLeadDetail } from '../../hooks/useLeadDetail'
import {
  deleteLead,
  listLeadOwners,
  toggleLeadArchived,
  updateLeadOwner
} from '../../services/leadsService'

const { FiX } = FiIcons

const useEscapeClose = (open, onClose) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

/**
 * Focused actions modal: change owner, archive/unarchive, delete.
 * @param {{
 *   leadId?: string | null,
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onActionComplete?: (payload?: { leadId?: string, removed?: boolean }) => void
 * }} props
 */
const LeadActionsModal = ({ leadId, isOpen, onClose, onActionComplete }) => {
  const { role, profile, user } = useAuth()
  const auth = useMemo(
    () => ({
      role: profile?.role ?? role,
      userId: profile?.id ?? user?.id
    }),
    [profile?.role, profile?.id, role, user?.id]
  )

  const [ownerId, setOwnerId] = useState('')
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false)
  const [savingOwner, setSavingOwner] = useState(false)
  const [togglingArchive, setTogglingArchive] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const ownerFieldRef = useRef(null)

  useEscapeClose(isOpen, onClose)

  const { data: lead, isLoading: leadLoading } = useLeadDetail(leadId, auth, {
    enabled: isOpen && Boolean(leadId)
  })

  const { data: owners = [], isLoading: ownersLoading } = useQuery({
    queryKey: ['lead-owners', auth.role, auth.userId],
    queryFn: async () => {
      const { data, error } = await listLeadOwners(auth)
      if (error) throw error
      return data ?? []
    },
    enabled: isOpen && Boolean(auth.userId),
    staleTime: 1000 * 60 * 5
  })

  const currentAgentId = lead?.agent_id ?? lead?.agentId ?? ''
  const isArchived = Boolean(lead?.archived)

  useEffect(() => {
    if (!isOpen || !lead) return
    setOwnerId(currentAgentId)
    setOwnerSearch('')
    setOwnerDropdownOpen(false)
    setDeleteConfirm(false)
  }, [isOpen, lead, currentAgentId])

  useEffect(() => {
    if (!ownerDropdownOpen) return undefined
    const onDoc = (e) => {
      if (ownerFieldRef.current && !ownerFieldRef.current.contains(e.target)) {
        setOwnerDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [ownerDropdownOpen])

  const filteredOwners = useMemo(() => {
    const q = ownerSearch.trim().toLowerCase()
    if (!q) return owners
    return owners.filter((u) => (u.full_name || u.email || '').toLowerCase().includes(q))
  }, [owners, ownerSearch])

  const selectedOwner = owners.find((u) => u.id === ownerId)

  const handleSaveOwner = async () => {
    if (!leadId || !ownerId) {
      toast.error('Lead owner is required')
      return
    }
    if (ownerId === currentAgentId) {
      toast.error('Select a different owner to save')
      return
    }
    setSavingOwner(true)
    try {
      const { error } = await updateLeadOwner(leadId, ownerId, auth)
      if (error) throw error
      toast.success('Lead owner updated')
      onActionComplete?.({ leadId })
      onClose()
    } catch (err) {
      toast.error(err?.message ?? 'Failed to update lead owner')
    } finally {
      setSavingOwner(false)
    }
  }

  const handleToggleArchive = async () => {
    if (!leadId) return
    setTogglingArchive(true)
    try {
      const nextArchived = !isArchived
      const { error } = await toggleLeadArchived(leadId, nextArchived, auth)
      if (error) throw error
      toast.success(nextArchived ? 'Lead archived' : 'Lead restored to pipeline')
      onActionComplete?.({ leadId, removed: nextArchived })
      onClose()
    } catch (err) {
      toast.error(err?.message ?? 'Failed to update archive status')
    } finally {
      setTogglingArchive(false)
    }
  }

  const handleDelete = async () => {
    if (!leadId) return
    setDeleting(true)
    try {
      const { error } = await deleteLead(leadId, auth)
      if (error) throw error
      toast.success('Lead deleted')
      onActionComplete?.({ leadId, removed: true })
      onClose()
    } catch (err) {
      toast.error(err?.message ?? 'Failed to delete lead')
    } finally {
      setDeleting(false)
      setDeleteConfirm(false)
    }
  }

  if (!isOpen) return null

  const leadName = lead?.full_name?.trim() || lead?.name?.trim() || 'Lead'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Lead actions</h3>
            <p className="text-sm text-slate-500 mt-0.5 truncate">{leadName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {leadLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#00AAFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !lead ? (
          <p className="p-6 text-sm text-red-600 text-center">Lead not found.</p>
        ) : (
          <div className="p-5 space-y-6">
            <section>
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Change Lead Owner</h4>
              <div className="relative" ref={ownerFieldRef}>
                <input
                  type="text"
                  value={ownerDropdownOpen ? ownerSearch : selectedOwner?.full_name || ''}
                  onChange={(e) => {
                    setOwnerSearch(e.target.value)
                    setOwnerDropdownOpen(true)
                  }}
                  onFocus={() => setOwnerDropdownOpen(true)}
                  placeholder={ownersLoading ? 'Loading users…' : 'Search by name'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#00AAFF]"
                  autoComplete="off"
                />
                {ownerDropdownOpen && (
                  <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg">
                    {ownersLoading ? (
                      <li className="px-4 py-2 text-sm text-slate-500">Loading users…</li>
                    ) : filteredOwners.length === 0 ? (
                      <li className="px-4 py-2 text-sm text-slate-500">No users found</li>
                    ) : (
                      filteredOwners.map((u) => (
                        <li key={u.id}>
                          <button
                            type="button"
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${
                              u.id === ownerId ? 'bg-sky-50 text-[#00AAFF]' : 'text-slate-700'
                            }`}
                            onClick={() => {
                              setOwnerId(u.id)
                              setOwnerSearch('')
                              setOwnerDropdownOpen(false)
                            }}
                          >
                            {u.full_name?.trim() || u.email || u.id}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={handleSaveOwner}
                disabled={savingOwner || !ownerId || ownerId === currentAgentId}
                className="mt-3 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                {savingOwner ? 'Saving…' : 'Save owner'}
              </button>
            </section>

            <section className="border-t border-gray-100 pt-5">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Archive</h4>
              <button
                type="button"
                onClick={handleToggleArchive}
                disabled={togglingArchive}
                className="w-full px-4 py-2 text-sm font-medium text-slate-700 border border-gray-200 rounded-xl hover:bg-slate-50 disabled:opacity-50"
              >
                {togglingArchive
                  ? 'Updating…'
                  : isArchived
                    ? 'Unarchive Lead'
                    : 'Archive Lead'}
              </button>
            </section>

            {auth.role === 'admin' && (
              <section className="border-t border-gray-100 pt-5">
                <h4 className="text-sm font-semibold text-slate-800 mb-3">Delete</h4>
                {!deleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(true)}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50"
                  >
                    Delete Lead
                  </button>
                ) : (
                  <div className="rounded-xl border border-red-100 bg-red-50/50 p-4 space-y-3">
                    <p className="text-sm text-red-800">
                      Are you sure? This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(false)}
                        disabled={deleting}
                        className="flex-1 px-3 py-2 text-sm font-medium text-slate-600 border border-gray-200 rounded-lg hover:bg-white"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleting}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {deleting ? 'Deleting…' : 'Confirm delete'}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default LeadActionsModal
